import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import type {
  SearchTasksByCompanyQuery,
  SearchTasksByContactQuery,
  SearchLooseyTasksQuery,
  Task
} from '@paintscout/api'
import { useSearchTasksByCompanyQuery, useSearchTasksByContactQuery, useSearchLooseyTasksQuery } from '@paintscout/api'
import { Sentry } from 'src/util/appSentry'
import { useSnackbar } from 'notistack'
import TaskTable from './TaskTable'

const FETCH_POLICY_CACHE_AND_NETWORK = 'cache-and-network'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    taskAggregateContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.grey[200]}`,
      borderRadius: theme.borderRadius.md,
      // all but first div get a left border
      '& > div:not(:first-child)': {
        borderLeft: `1px solid ${theme.palette.grey[200]}`,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2)
      },
      '& > div:first-child': {
        paddingRight: theme.spacing(2)
      },
      '& > div:last-child': {
        paddingRight: theme.spacing(0)
      }
    },
    aggregateSection: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }
  }),
  { name: 'TaskDocList' }
)

export interface TaskDocListProps {
  classes?: StyleClasses<typeof useStyles>
  contactId?: string
  companyId?: string
  query?: string
  dense?: boolean
  limit?: number
  title?: string
  dashboard?: boolean
  enableFade?: boolean
  view?: 'upcoming' | 'overdue' | 'completed' | 'all' // used with enableFade to determine which tasks to fade
  page?: number
  sort?: string
  showSinglePagePagination?: boolean
  handleRequestSort?: (ev: React.MouseEvent, name: string, direction: 'asc' | 'desc') => void
  onPageChange?: (newPage: number) => void
  onNewClick?: () => void
  handleCreate?: () => void
  onTasksChange?: () => void
  handleAddTaskTemplate?: (templateId: string) => void
  newTasks?: Task[] | null
}

function TaskDocList({
  classes: classesProp,
  contactId,
  companyId,
  query,
  dense = true,
  dashboard = false,
  enableFade,
  view,
  limit: argsLimit = 5,
  title,
  handleCreate,
  onTasksChange,
  page = 1,
  sort = 'startDateTime',
  showSinglePagePagination,
  onPageChange,
  newTasks,
  handleRequestSort = (_ev: React.MouseEvent, _name: string, _direction: 'asc' | 'desc') => null
}: TaskDocListProps) {
  const classes = useStyles({ classes: classesProp })
  const [currentBookmark, setCurrentBookmark] = useState(null)
  const [currentPage, setCurrentPage] = useState(page)
  const { enqueueSnackbar } = useSnackbar()
  const limit = argsLimit

  const {
    data: tasksData,
    loading: fetchingTasksData,
    error: tasksError,
    fetchMore: fetchMoreTasks,
    updateQuery: updateTasksQuery
  } = useSearchLooseyTasksQuery({
    variables: { query, sort, limit },
    fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK,
    skip: !!(contactId || companyId)
  })

  const {
    data: tasksByCompanyData,
    loading: fetchingByCompany,
    error: tasksByCompanyError,
    fetchMore: fetchMoreTasksByCompany,
    updateQuery: updateTasksByCompanyQuery
  } = useSearchTasksByCompanyQuery({
    variables: { companyId: companyId, sort, limit },
    fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK,
    skip: !companyId
  })

  const {
    data: tasksByContactData,
    loading: fetchingByContact,
    error: tasksByContactError,
    fetchMore: fetchMoreTasksByContact,
    updateQuery: updateTasksByContactQuery
  } = useSearchTasksByContactQuery({
    variables: { contactId: contactId, sort, limit },
    fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK,
    skip: !contactId
  })

  const { tasks, totalRows } = useMemo(() => {
    setCurrentBookmark(
      tasksByCompanyData?.searchTasksByCompany?.bookmark ??
        tasksByContactData?.searchTasksByContact?.bookmark ??
        tasksData?.searchTasks?.bookmark ??
        null
    )
    const tasks =
      tasksByCompanyData?.searchTasksByCompany?.tasks ??
      tasksByContactData?.searchTasksByContact?.tasks ??
      tasksData?.searchTasks?.tasks ??
      []
    const filteredTasks = tasks
      .filter((task) => {
        if (view === 'upcoming') {
          return !task.completed && (!task.startDateTime || new Date(task.startDateTime).getTime() >= Date.now())
        } else if (view === 'overdue') {
          return !task.completed && task.startDateTime && new Date(task.startDateTime).getTime() < Date.now()
        } else if (view === 'completed') {
          return task.completed
        }
        return true
      })
      .sort((a, b) => {
        const isDesc = sort?.startsWith('-')
        if (!a.startDateTime && !b.startDateTime) return 0
        if (!a.startDateTime) return isDesc ? 1 : -1
        if (!b.startDateTime) return isDesc ? -1 : 1
        const diff = new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
        return isDesc ? -diff : diff
      })
    const rows =
      tasksByCompanyData?.searchTasksByCompany?.total_rows ??
      tasksByContactData?.searchTasksByContact?.total_rows ??
      tasksData?.searchTasks?.total_rows ??
      0
    const totalRows = rows - (tasks.length - filteredTasks.length)
    return {
      tasks: filteredTasks,
      totalRows
    }
  }, [tasksByCompanyData, tasksByContactData, tasksData])

  // Slice tasks for current page display
  const paginatedTasks = useMemo(() => {
    const startIndex = (currentPage - 1) * limit
    const endIndex = startIndex + limit
    return tasks.slice(startIndex, endIndex)
  }, [tasks, currentPage, limit])

  const loading = useMemo(
    () => fetchingByCompany || fetchingByContact || fetchingTasksData,
    [fetchingByCompany, fetchingByContact, fetchingTasksData]
  )
  useEffect(() => {
    if (page == 1 && page !== currentPage) {
      setCurrentPage(page)
    }
  }, [page])

  // Insert tasks created elsewhere (e.g. global header's New Task button) into the SearchLooseyTasks
  // cache. cache-and-network can race with ES indexing and overwrite the automatic cache update from
  // updateCachedTask, so we re-apply on every change to newTasks until the network catches up.
  useEffect(() => {
    if (!newTasks?.length) return
    if (contactId || companyId) return

    const sortTasks = (tasks: Task[]) =>
      [...tasks].sort((a, b) => {
        const aHasDate = !!a.startDateTime
        const bHasDate = !!b.startDateTime
        if (aHasDate && !bHasDate) return -1
        if (!aHasDate && bHasDate) return 1
        if (!aHasDate && !bHasDate) return 0
        return new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
      })

    updateTasksQuery((cache: SearchLooseyTasksQuery) => {
      if (!cache?.searchTasks) return cache
      const existingIds = new Set(cache.searchTasks.tasks.map((t) => t._id))
      const incoming = newTasks.filter((t) => !existingIds.has(t._id))
      if (!incoming.length) return cache
      return {
        ...cache,
        searchTasks: {
          ...cache.searchTasks,
          tasks: sortTasks([...cache.searchTasks.tasks, ...incoming]),
          total_rows: cache.searchTasks.total_rows + incoming.length
        }
      }
    })
  }, [newTasks]) // eslint-disable-line react-hooks/exhaustive-deps

  if (tasksByCompanyError || tasksByContactError || tasksError) {
    Sentry.captureException(tasksByCompanyError || tasksByContactError || tasksError, {
      tags: { contactId, companyId }
    })
    enqueueSnackbar('Unable to load Tasks', { variant: 'error' })
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
    onPageChange?.(newPage)
    if (newPage === 1) {
      setCurrentBookmark(null)
    }
  }

  const handleFetchNextPage = (page: number) => {
    const nextPageFirstIndex = (page - 1) * limit
    const dataExists = !!tasks[nextPageFirstIndex]
    if (dataExists) {
      return
    }
    if (companyId) {
      fetchMoreTasksByCompany({
        variables: {
          limit: limit,
          from: currentPage * limit,
          bookmark: currentBookmark
        },
        updateQuery: (prev: SearchTasksByCompanyQuery, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          setCurrentBookmark(fetchMoreResult.searchTasksByCompany.bookmark)
          return Object.assign({}, prev, {
            searchTasksByCompany: {
              ...prev.searchTasksByCompany,
              tasks: [...prev.searchTasksByCompany.tasks, ...fetchMoreResult.searchTasksByCompany.tasks],
              bookmark: fetchMoreResult.searchTasksByCompany.bookmark
            }
          })
        }
      })
    } else if (contactId) {
      fetchMoreTasksByContact({
        variables: {
          limit: limit,
          from: currentPage * limit,
          bookmark: currentBookmark
        },
        updateQuery: (prev: SearchTasksByContactQuery, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          setCurrentBookmark(fetchMoreResult.searchTasksByContact.bookmark)
          return Object.assign({}, prev, {
            searchTasksByContact: {
              ...prev.searchTasksByContact,
              tasks: [...prev.searchTasksByContact.tasks, ...fetchMoreResult.searchTasksByContact.tasks],
              bookmark: fetchMoreResult.searchTasksByContact.bookmark
            }
          })
        }
      })
    } else {
      fetchMoreTasks({
        variables: {
          limit: limit,
          from: currentPage * limit,
          bookmark: currentBookmark
        },
        updateQuery: (prev: SearchLooseyTasksQuery, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          setCurrentBookmark(fetchMoreResult.searchTasks.bookmark)
          return Object.assign({}, prev, {
            searchTasks: {
              ...prev.searchTasks,
              tasks: [...prev.searchTasks.tasks, ...fetchMoreResult.searchTasks.tasks],
              bookmark: fetchMoreResult.searchTasks.bookmark
            }
          })
        }
      })
    }
  }

  function removeRowsFromQuery(ids: string[]) {
    onTasksChange?.()
    if (companyId) {
      updateTasksByCompanyQuery((cache: SearchTasksByCompanyQuery) => {
        if (!cache?.searchTasksByCompany) {
          return cache
        }

        return {
          ...cache,
          searchTasksByCompany: {
            ...cache.searchTasksByCompany,
            tasks: cache.searchTasksByCompany.tasks.filter((task) => !ids.includes(task._id))
          }
        }
      })
    } else if (contactId) {
      updateTasksByContactQuery((cache: SearchTasksByContactQuery) => {
        if (!cache?.searchTasksByContact) {
          return cache
        }

        return {
          ...cache,
          searchTasksByContact: {
            ...cache.searchTasksByContact,
            tasks: cache.searchTasksByContact.tasks.filter((task) => !ids.includes(task._id))
          }
        }
      })
    } else {
      updateTasksQuery((cache: SearchLooseyTasksQuery) => {
        if (!cache?.searchTasks) {
          return cache
        }
        console.log('we in here removing', { ids, tasks: cache.searchTasks.tasks })
        return {
          ...cache,
          searchTasks: {
            ...cache.searchTasks,
            tasks: cache.searchTasks.tasks.filter((task) => !ids.includes(task._id))
          }
        }
      })
    }
  }

  function updateRowsInQuery(task: any) {
    onTasksChange?.()
    if (companyId) {
      updateTasksByCompanyQuery((cache: SearchTasksByCompanyQuery) => {
        if (!cache?.searchTasksByCompany) {
          return cache
        }

        return {
          ...cache,
          searchTasksByCompany: {
            ...cache.searchTasksByCompany,
            tasks: cache.searchTasksByCompany.tasks
              .map((t) => (t._id === task._id ? task : t))
              .sort((a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime())
          }
        }
      })
    } else if (contactId) {
      updateTasksByContactQuery((cache: SearchTasksByContactQuery) => {
        if (!cache?.searchTasksByContact) {
          return cache
        }

        return {
          ...cache,
          searchTasksByContact: {
            ...cache.searchTasksByContact,
            tasks: cache.searchTasksByContact.tasks
              .map((t) => (t._id === task._id ? task : t))
              .sort((a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime())
          }
        }
      })
    } else {
      updateTasksQuery((cache: SearchLooseyTasksQuery) => {
        if (!cache?.searchTasks) {
          return cache
        }

        return {
          ...cache,
          searchTasks: {
            ...cache.searchTasks,
            tasks: cache.searchTasks.tasks
              .map((t) => (t._id === task._id ? task : t))
              .sort((a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime())
          }
        }
      })
    }
  }

  return (
    <div className={classes?.root}>
      <TaskTable
        classes={classes}
        dashboard={dashboard}
        handleCreate={handleCreate}
        title={title}
        tasks={paginatedTasks}
        loading={loading}
        dense={dense}
        totalRows={totalRows}
        page={currentPage}
        sort={sort}
        rowsPerPage={limit}
        pages={Math.ceil(totalRows / limit)}
        disablePagination={!showSinglePagePagination && totalRows <= limit}
        onPageChange={handlePageChange}
        allowCustomColumns={true}
        actions={{
          edit: true,
          viewDeal: true,
          delete: true
        }}
        actionsForRow={(row) => ({
          delete: true,
          viewDeal: !!row.dealId,
          edit: true
        })}
        onFetchNextPage={handleFetchNextPage}
        onDelete={removeRowsFromQuery}
        onEdit={updateRowsInQuery}
        onComplete={onTasksChange}
        onRequestSort={handleRequestSort}
        enableFade={enableFade}
        view={view}
        noResultsMessage={'No tasks found.'}
      />
    </div>
  )
}

export default TaskDocList
